import { BaseSyntheticEvent } from "react";
import { AxiosError } from "axios";
import { useAuthorizationContext, useClientPortalConfigContext, useResetPasswordContext } from "../../../../../context";
import {
    AccountModel,
    ResetPasswordModel,
    ResetPasswordFailedApiDetailsErrorResponse,
} from "../../../../../types/generated/models";
import { UseFormReturn } from "react-hook-form";
import { ResetPasswordType, Step } from "./ResetPasswordDialogForm.helpers";
import { enqueueSnackbar } from "@powerdiary/components-library/lib/components/Snackbar";
import { Typography } from "@mui/material";
import { usePostMutation } from "../../../../../hooks/useClientPortalQuery";
import { useAnalytics } from "../../../../../hooks/useAnalytics";

export interface IUserRegisterHook {
    isPending: boolean;
    resetPassword: (values: ResetPasswordType, event: BaseSyntheticEvent) => Promise<void>;
}

export function useClientPortalResetPassword(form: UseFormReturn<ResetPasswordType>): IUserRegisterHook {
    const { clientPortalGuid } = useClientPortalConfigContext();
    const { setShowResetPasswordDialog, setClientPublicId } = useResetPasswordContext();
    const { getValues, setValue, reset, setError } = form;
    const { loginUser } = useAuthorizationContext();
    const { track } = useAnalytics();

    const { mutateAsync, isPending } = usePostMutation<AccountModel, AxiosError, ResetPasswordModel>({
        url: `/api/v1/${clientPortalGuid}/Auth/ResetPassword`,
    });

    const resetPassword = async (values: ResetPasswordType, event: BaseSyntheticEvent) => {
        try {
            const response = await mutateAsync(values as ResetPasswordModel);

            if (values.step === Step.Initial) {
                setValue("step", Step.Verification);
                event.preventDefault();
            } else if (response.clientId) {
                loginUser(response);
                track("password_reset");
                setShowResetPasswordDialog(false);
                setClientPublicId(undefined);
                enqueueSnackbar(<Typography variant="body2">You password has been updated.</Typography>, {
                    variant: "success",
                    alertTitle: "Password reset successful",
                });
            }
        } catch (error) {
            const typedError = error as AxiosError<ResetPasswordFailedApiDetailsErrorResponse>;
            if (typedError.response.status === 400) {
                if (typedError.response?.data?.details?.tokenError && values.step === Step.Initial) {
                    setValue("step", Step.Verification);
                }
                setError("root", {
                    type: "manual",
                    message: typedError.response?.data.message || "An error occurred during resetting password.",
                });
            }
        } finally {
            const formValues = getValues();
            reset(formValues, {
                keepErrors: true,
            });
        }
    };

    return {
        isPending,
        resetPassword,
    };
}
